import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, from, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import {
  DeleteWorkspace,
  type IDeleteWorkspaceHandler,
  type IRetrieveWorkspaceQuery,
  RetrieveWorkspaceOutput,
} from '@telespot/management/use-cases';
import { WorkspaceDetailData, WorkspaceDetailState } from './workspace-detail.state';
import { WorkspacesState } from './workspaces.state';

export const RETRIEVE_WORKSPACE_QUERY = new InjectionToken<IRetrieveWorkspaceQuery>('RETRIEVE_WORKSPACE_QUERY');
export const DELETE_WORKSPACE_HANDLER = new InjectionToken<IDeleteWorkspaceHandler>('DELETE_WORKSPACE_HANDLER');

@Injectable()
export class WorkspaceDetailEffects extends ComponentStore<Record<string, never>> {
  /**
   * Emits true on successful deletion, the deletion error message on failure.
   * Lets the facade react to delete completion without polling state observables.
   */
  private readonly _deleteResult$ = new Subject<{ success: boolean; error?: string }>();
  public readonly deleteResult$ = this._deleteResult$.asObservable();

  constructor(
    private readonly detailState: WorkspaceDetailState,
    private readonly workspacesState: WorkspacesState,
    @Inject(RETRIEVE_WORKSPACE_QUERY) private readonly retrieveQuery: IRetrieveWorkspaceQuery,
    @Inject(DELETE_WORKSPACE_HANDLER) private readonly deleteHandler: IDeleteWorkspaceHandler
  ) {
    super({});
  }

  // ============================================================================
  // Selectors — re-exposed from WorkspaceDetailState
  // ============================================================================

  public get id$() {
    return this.detailState.id$;
  }
  public get organizationId$() {
    return this.detailState.organizationId$;
  }
  public get data$() {
    return this.detailState.data$;
  }
  public get loading$() {
    return this.detailState.loading$;
  }
  public get error$() {
    return this.detailState.error$;
  }
  public get deleting$() {
    return this.detailState.deleting$;
  }
  public get deletionError$() {
    return this.detailState.deletionError$;
  }
  public get numCases$() {
    return this.detailState.numCases$;
  }
  public get numMembers$() {
    return this.detailState.numMembers$;
  }
  public get caseTypeName$() {
    return this.detailState.caseTypeName$;
  }

  // ============================================================================
  // Updater passthroughs
  // ============================================================================

  public readonly clearActiveWorkspace = () => this.detailState.clear();

  // ============================================================================
  // Effects
  // ============================================================================

  public readonly loadDetail = this.detailState.effect((action$: Observable<{ workspaceId: string }>) =>
    action$.pipe(
      tap(() => this.detailState.setLoading(true)),
      switchMap(({ workspaceId }) =>
        from(this.retrieveQuery.execute(workspaceId)).pipe(
          tap((workspace: RetrieveWorkspaceOutput | undefined) => {
            if (!workspace) {
              this.detailState.setError('workspaces.detail.error.loading');
              return;
            }
            const detailData: WorkspaceDetailData = {
              id: workspace.id,
              name: workspace.name,
              organizationId: workspace.organizationId,
              caseTypeId: workspace.caseTypeId,
              caseTypeName: workspace.caseTypeName,
              updatedAt: workspace.referenceDate,
              usedStorage: workspace.usedStorage,
              config: workspace.config,
              numMembers: workspace.numMembers,
              numCases: workspace.numCases,
            };
            this.detailState.setData({
              id: workspace.id,
              organizationId: workspace.organizationId,
              data: detailData,
            });
          }),
          catchError(() => {
            this.detailState.setError('workspaces.detail.error.loading');
            return of(undefined);
          })
        )
      )
    )
  );

  /**
   * Re-fetch the active workspace and merge fresh denormalized fields into state.
   * Best-effort: errors are swallowed so a transient failure doesn't blow up the UI.
   * Does not toggle the loading flag.
   */
  public readonly refreshWorkspace = this.detailState.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => this.detailState.id$),
      switchMap(([, workspaceId]) => {
        if (!workspaceId) return of(undefined);
        return from(this.retrieveQuery.execute(workspaceId)).pipe(
          tap((fresh: RetrieveWorkspaceOutput | undefined) => {
            if (!fresh) return;
            this.detailState.mergeData({
              name: fresh.name,
              caseTypeId: fresh.caseTypeId,
              caseTypeName: fresh.caseTypeName,
              numCases: fresh.numCases,
              numMembers: fresh.numMembers,
              usedStorage: fresh.usedStorage,
              config: fresh.config,
            });
          }),
          catchError(() => of(undefined))
        );
      })
    )
  );

  public readonly deleteWorkspace = this.detailState.effect((workspaceId$: Observable<string>) =>
    workspaceId$.pipe(
      tap(() => {
        this.detailState.setDeletionError(undefined);
        this.detailState.setDeleting(true);
      }),
      switchMap((workspaceId) =>
        from(this.deleteHandler.execute(new DeleteWorkspace({ workspaceId }))).pipe(
          tap((success) => {
            if (success) {
              this.workspacesState.removeItem(workspaceId);
              this.detailState.setDeleting(false);
              this._deleteResult$.next({ success: true });
            } else {
              this.detailState.setDeletionError('workspaces.detail.error.deletion');
              this._deleteResult$.next({ success: false, error: 'workspaces.detail.error.deletion' });
            }
          }),
          catchError(() => {
            this.detailState.setDeletionError('workspaces.detail.error.deletion');
            this._deleteResult$.next({ success: false, error: 'workspaces.detail.error.deletion' });
            return EMPTY;
          })
        )
      )
    )
  );
}
